import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CreateTemplate from './CreateTemplate';
import TemplateHistory from './TemplateHistory';
import EmailSequenceBuilder from './EmailSequenceBuilder';
import { Button } from './styles';

// Local storage key for storing email templates
const STORAGE_KEY = 'email_templates';

const EmailTemplates = () => {
  // State for templates list
  const [templates, setTemplates] = useState([]);
  const [activeView, setActiveView] = useState('create');
  
  // Template editing state
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Load templates from local storage on component mount
  useEffect(() => {
    const savedTemplates = localStorage.getItem(STORAGE_KEY);
    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    }
  }, []);

  // Save templates to local storage whenever templates change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  }, [templates]);

  // Add or update template
  const saveTemplate = (newTemplate) => {
    if (isEditing && activeTemplate) {
      // Update existing template
      setTemplates(templates.map(template => 
        template.id === activeTemplate.id ? { 
          ...newTemplate, 
          id: activeTemplate.id,
          createdAt: activeTemplate.createdAt,
          updatedAt: new Date().toISOString()
        } : template
      ));
    } else {
      // Add new template
      setTemplates([...templates, {
        ...newTemplate,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }]);
    }
    
    // Reset editing state and switch to history view
    resetEditingState();
    setActiveView('history');
  };

  // Edit template
  const editTemplate = (template) => {
    setActiveTemplate(template);
    setIsEditing(true);
    setActiveView('create');
  };

  // Delete template
  const deleteTemplate = (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      setTemplates(templates.filter(template => template.id !== id));
      
      // If the active template is deleted, reset the editing state
      if (activeTemplate && activeTemplate.id === id) {
        resetEditingState();
      }
    }
  };

  // Reset editing state
  const resetEditingState = () => {
    setActiveTemplate(null);
    setIsEditing(false);
  };

  // Create New Template button handler
  const handleCreateNew = () => {
    resetEditingState();
    setActiveView('create');
  };

  // Render appropriate view based on active tab
  const renderView = () => {
    switch (activeView) {
      case 'create':
        return (
          <CreateTemplate 
            onSave={saveTemplate}
            template={activeTemplate}
            isEditing={isEditing}
            onCancel={resetEditingState}
          />
        );
      case 'history':
        return (
          <TemplateHistory 
            templates={templates}
            onEdit={editTemplate}
            onDelete={deleteTemplate}
            onCreateNew={handleCreateNew}
          />
        );
      case 'sequences':
        return <EmailSequenceBuilder />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <h1>Email Marketing Suite</h1>
      
      <TabsContainer>
        <Tab 
          active={activeView === 'create'} 
          onClick={() => setActiveView('create')}
        >
          {isEditing ? 'Edit Template' : 'Create Template'}
        </Tab>
        <Tab 
          active={activeView === 'history'} 
          onClick={() => setActiveView('history')}
        >
          Template Library
        </Tab>
        <Tab 
          active={activeView === 'sequences'} 
          onClick={() => setActiveView('sequences')}
        >
          Sequence Builder
        </Tab>
      </TabsContainer>
      
      {renderView()}
    </Container>
  );
};

export default EmailTemplates;

// Component-specific styled components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  
  h1 {
    margin-bottom: 20px;
    color: #333;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 30px;
  border-bottom: 1px solid #e0e0e0;
`;

const Tab = styled.div`
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 3px solid ${props => props.active ? '#2196f3' : 'transparent'};
  color: ${props => props.active ? '#2196f3' : '#666'};
  
  &:hover {
    color: #2196f3;
    background-color: #f5f5f5;
  }
`;
